import React, { useEffect } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';
import { observer } from 'mobx-react';

import { angularizeWithProviders } from '../../../../../shared/providers';
import { InbodyIndicators } from './InbodyIndicators/InbodyIndicators';
import { InbodyIndicatorsFormValues } from './NgzInbodyIndicatorsTab.settings';
import { InbodyType } from '../../../../../shared/constants/inbody-info/InbodyType';
import { setDateRangeToParams } from './utils/setDateRangeToParams';
import { inbodyIndicatorsStore } from '../../../../stores/inbodyIndicatorsStore';
import { useAutoSubmitForm } from '../../../../../shared/hooks/useAutoSubmitForm';
import { CustomerType } from '../../../../../shared/constants/customer/customerType';

// personal fields are null for incomplete (NEED_SETUP) ScanIQ customers
type InbodyIndicatorsTabProps = {
  customerId: number;
  firstName: string | null;
  lastName: string | null;
  age: number | null;
  gender: string | null;
  height: number | null;
  inbodyId: string;
  inbodyType?: InbodyType;
  customerType: CustomerType;
  entityId?: number;
};

/*
 Backend filters scans by inbodyType, frontend filters scans by startScanId and endScanId for table and chart.
 Scan orders from oldest to newest. (default for chart, in table we have to sort functionality by scanDateTime)
 ScansMetadata is all scans sorted from newest to oldest. Frontend needs to filter it by inbodyType and use as options in filter.
 Backend returns config for every indicator; scans is null when a metric is not produced for the customer's machine type (rendered as an empty state).

 CustomerType Integration:
 The customerType parameter determines which scan service to use (set in store):
 - NUTRITION: Uses nutritionScanService, fetches meal plans data.
 - SCAN_IQ: Uses scanIqScanService, no meal plans data.


 General Functionality:
 1. When inbody type changes we need to change scans range and get new inbody indicators.
 2. Need to synchronize range with other tabs. On init and when scan range changes we need to filter scans and set new range filter in url params.
    (so scans customer can see same range in all tabs)
 3. Show All Scans - when in this mode need to show all scans (don't reset filter data only disable filters) after turning off show filtered scans.
    (show all scans for selected inbody type)
 4. Save filtered data after leaving tab, reset only on reload page or for new customer.

 Code flow:
  use store to save data after unmount and init data on mount (only when customerId or customerType changes).
  Store is responsible for setting default filter data and changing scans range after inbody type change. 
  It changes filterData value and Form changes it in inputs because values: filterData config.
  Store uses customerType to select appropriate scan service and conditionally fetch customer-type-specific data.
*/

const InbodyIndicatorsTab = observer(
  ({
    customerId,
    firstName,
    lastName,
    age,
    gender,
    height,
    inbodyId,
    inbodyType,
    customerType,
    entityId,
  }: InbodyIndicatorsTabProps) => {
    const {
      inbodyTypeOptions,
      scansMetadata,
      mealPlansOnScanDay,
      inbodyIndicators,
      filteredInbodyIndicators,
      availableScansMetadata,
      filterData,
      showAllScans,
      toggleShowAllScans,
      initData,
      applyFilters,
    } = inbodyIndicatorsStore;

    const formMethods = useForm<InbodyIndicatorsFormValues>({
      values: filterData,
    });
    const { watch, handleSubmit } = formMethods;

    useAutoSubmitForm(handleSubmit(applyFilters), watch);

    const { startScanId, endScanId } = watch();

    useUpdateEffect(() => {
      setDateRangeToParams(scansMetadata, startScanId, endScanId);
    }, [startScanId, endScanId]);

    useEffect(() => {
      initData(customerId, customerType, entityId);
    }, [customerId, customerType, entityId]);

    return (
      <FormProvider {...formMethods}>
        <InbodyIndicators
          customerInbodyIndicators={
            showAllScans ? inbodyIndicators : filteredInbodyIndicators
          }
          allInbodyIndicators={inbodyIndicators}
          mealPlansOnScanDay={mealPlansOnScanDay}
          inbodyTypeOptions={inbodyTypeOptions}
          availableScansMetadata={availableScansMetadata}
          customerInfo={{
            id: customerId,
            firstName,
            lastName,
            age,
            gender,
            height,
            inbodyId,
            inbodyType,
          }}
          customerType={customerType}
          showAllScans={showAllScans}
          entityId={entityId}
          onToggleShowAllScans={toggleShowAllScans}
        />
      </FormProvider>
    );
  },
);

angularizeWithProviders(InbodyIndicatorsTab, 'ngzInbodyIndicatorsTab', {
  customerId: '<',
  firstName: '<',
  lastName: '<',
  age: '<',
  gender: '<',
  height: '<',
  inbodyId: '<',
  inbodyType: '<',
  customerType: '@',
  entityId: '<',
});

export { InbodyIndicatorsTab };
